import type {DirectTurnKind, DirectTurnStartPath} from '@github-ui/ahp-session-client/contracts'
import {sendCustomMetric} from '@github-ui/stats'

import {commonTags, createEmitter, type MetricSender} from './browser-outcome-metrics'
import {modelFamily, type ModelFamily} from './model-family'
import {recordTaskStartType, recordTurnStartType, startTypeFromStartPath} from './start-type'
import {failed, NO_REASONS, unresolved, type OutcomeReasons, type UnresolvedReason} from './outcome-reasons'
import type {ResultPresentationFact} from './result-presentation-contract'
import {isOneCopilotBrowserTelemetryEnabled} from './telemetry-gate'
import type {UiReadinessRole, UiReadinessSurface} from './ui-readiness-contract'

export const TIME_TO_FIRST_CONTENT_PHASES = [
  'submit_to_dispatch',
  'dispatch_to_acknowledgement',
  'acknowledgement_to_first_content',
  'first_content_to_paint',
] as const
export type TimeToFirstContentPhase = (typeof TIME_TO_FIRST_CONTENT_PHASES)[number]
export type TurnTerminalOutcome = 'completed' | 'cancelled' | 'error'

type CompletedPresentation = Extract<ResultPresentationFact, {observation: 'completed'}>

/** Turn timing in `performance.timeOrigin + performance.now()` milliseconds. */
export interface TurnSubmission {
  readonly requestId: string
  readonly taskId?: string
  readonly surface?: UiReadinessSurface
  readonly turnKind: DirectTurnKind
  readonly startPath: DirectTurnStartPath
  readonly readyAtSubmit: boolean
  readonly modelId?: string
  readonly submittedAtMs: number
  /** False when submission timing came from an earlier document, such as a One Copilot entry. */
  readonly submittedInDocument: boolean
}

export interface TurnDispatch extends TurnSubmission {
  readonly turnId: string
  readonly dispatchedAtMs: number
  readonly attributionPending?: boolean
}

interface TurnRecord extends Omit<TurnDispatch, 'modelId' | 'taskId' | 'surface' | 'attributionPending'> {
  readonly modelFamily: ModelFamily
  taskId?: string
  surface?: UiReadinessSurface
  attributionPending?: boolean
  surfaceObserved?: UiReadinessSurface
  role?: UiReadinessRole
  acknowledgedAtMs?: number
  firstContentAtMs?: number
  contentReceipt?: boolean
  ttftFinal?: boolean
  terminalOutcome?: TurnTerminalOutcome
  terminalAtMs?: number
  terminalEmitted?: boolean
  pendingPresentation?: CompletedPresentation
  pendingContentEnd?: {outcome: 'failure' | 'unresolved'; reasons: OutcomeReasons}
}

interface PendingTurnRecord extends Omit<TurnSubmission, 'modelId'> {
  readonly modelFamily: ModelFamily
}

type TagSource = Omit<TurnSubmission, 'modelId'> & {
  readonly turnId?: string
  readonly modelFamily: ModelFamily
  readonly surfaceObserved?: UiReadinessSurface
  readonly role?: UiReadinessRole
}

const MAX_TURN_RECORDS = 256

function rememberFinalizedRequest(finalized: Set<string>, requestId: string) {
  finalized.add(requestId)
  if (finalized.size <= MAX_TURN_RECORDS) return
  const oldest = finalized.values().next().value
  if (oldest) finalized.delete(oldest)
}

export function createTurnMetrics({
  send = sendCustomMetric,
  reportError = () => undefined,
  timeOrigin = () => performance.timeOrigin,
  enabled = isOneCopilotBrowserTelemetryEnabled,
  pageExitTarget = typeof window === 'undefined' ? undefined : window,
}: {
  send?: MetricSender
  reportError?: () => void
  timeOrigin?: () => number
  enabled?: () => boolean
  pageExitTarget?: Pick<Window, 'addEventListener'>
} = {}) {
  const emit = createEmitter(send, reportError)
  const pending = new Map<string, PendingTurnRecord>()
  const records = new Map<string, TurnRecord>()
  const finalizedRequests = new Set<string>()
  let pageExitInstalled = false

  const turnTags = (record: TagSource) => ({
    ...commonTags(
      {
        surface: record.surfaceObserved ?? record.surface ?? 'unknown',
        role: record.role ?? 'unknown',
        task_id: record.taskId,
      },
      startTypeFromStartPath(record.startPath),
    ),
    turn_kind: record.turnKind,
    model_family: record.modelFamily,
  })
  // A turn that cold-starts or resumes reports how the task's connection started; an active turn
  // reuses that connection.
  const recordStart = (record: Pick<TurnDispatch, 'turnId' | 'taskId' | 'startPath'>) => {
    recordTurnStartType(record.turnId, startTypeFromStartPath(record.startPath))
    if (record.startPath !== 'active') recordTaskStartType(record.taskId, startTypeFromStartPath(record.startPath))
  }
  const ttftTags = (record: TagSource) => ({
    ...turnTags(record),
    ready_at_submit: record.readyAtSubmit ? 'ready' : 'waiting',
  })
  const rum = (record: TagSource) => ({
    ...(record.turnId ? {turn_id: record.turnId} : {}),
    request_id: record.requestId,
  })
  const emitTtftOutcome = (record: TagSource, outcome: 'success' | 'failure' | 'unresolved', reasons: OutcomeReasons) =>
    emit(
      {
        name: 'ONE_COPILOT_WEB_TIME_TO_FIRST_CONTENT_OUTCOME',
        value: 1,
        tags: {...ttftTags(record), outcome, ...reasons},
      },
      {task_id: record.taskId},
      rum(record),
    )

  const release = (record: TurnRecord) => {
    if (record.ttftFinal && record.terminalEmitted) records.delete(record.turnId)
  }
  const emitTerminal = (record: TurnRecord) => {
    if (
      record.attributionPending ||
      record.terminalEmitted ||
      !record.terminalOutcome ||
      record.terminalAtMs === undefined
    )
      return
    record.terminalEmitted = true
    const elapsed = record.terminalAtMs - record.submittedAtMs
    if (Number.isFinite(elapsed) && elapsed >= 0) {
      emit(
        {
          name: 'ONE_COPILOT_WEB_TURN_LATENCY',
          value: elapsed,
          tags: {...turnTags(record), outcome: record.terminalOutcome},
        },
        {task_id: record.taskId},
        rum(record),
      )
    }
    release(record)
  }
  const finalizeUnresolved = (record: TurnRecord, reason: UnresolvedReason) => {
    if (record.ttftFinal) return
    record.ttftFinal = true
    emitTtftOutcome(record, 'unresolved', unresolved(reason))
  }
  const finalizePending = (record: PendingTurnRecord, outcome: 'failure' | 'unresolved', reasons: OutcomeReasons) => {
    if (finalizedRequests.has(record.requestId)) return
    pending.delete(record.requestId)
    rememberFinalizedRequest(finalizedRequests, record.requestId)
    emitTtftOutcome(record, outcome, reasons)
  }
  const retire = (record: TurnRecord, fallback: UnresolvedReason) => {
    if (record.ttftFinal) return
    if (record.firstContentAtMs !== undefined || record.contentReceipt) {
      finalizeUnresolved(record, 'missing_evidence')
    } else if (record.terminalOutcome === 'error') {
      record.ttftFinal = true
      emitTtftOutcome(record, 'failure', failed('turn_error'))
    } else if (record.terminalOutcome === 'cancelled') {
      finalizeUnresolved(record, 'cancelled')
    } else if (record.terminalOutcome === 'completed') {
      finalizeUnresolved(record, 'no_content')
    } else {
      finalizeUnresolved(record, fallback)
    }
  }
  const present = (record: TurnRecord, completion: CompletedPresentation) => {
    if (record.ttftFinal) return
    if (record.attributionPending) {
      record.pendingPresentation = completion
      return
    }
    record.ttftFinal = true
    record.surfaceObserved = completion.surface
    record.role = completion.role
    const presentedAtMs = timeOrigin() + completion.closure_time_ms
    const elapsed = presentedAtMs - record.submittedAtMs
    if (!Number.isFinite(elapsed) || elapsed < 0) {
      emitTtftOutcome(record, 'unresolved', unresolved('other'))
      release(record)
      return
    }
    const fact = {task_id: record.taskId}
    emit(
      {name: 'ONE_COPILOT_WEB_TIME_TO_FIRST_CONTENT_LATENCY', value: elapsed, tags: ttftTags(record)},
      fact,
      rum(record),
    )
    const phases: Array<[TimeToFirstContentPhase, number | undefined, number | undefined]> = [
      ['submit_to_dispatch', record.submittedInDocument ? record.submittedAtMs : undefined, record.dispatchedAtMs],
      ['dispatch_to_acknowledgement', record.dispatchedAtMs, record.acknowledgedAtMs],
      ['acknowledgement_to_first_content', record.acknowledgedAtMs, record.firstContentAtMs],
      ['first_content_to_paint', record.firstContentAtMs, presentedAtMs],
    ]
    for (const [phase, start, end] of phases) {
      if (start === undefined || end === undefined || end < start) continue
      emit(
        {
          name: 'ONE_COPILOT_WEB_TIME_TO_FIRST_CONTENT_PHASE_LATENCY',
          value: end - start,
          tags: {...ttftTags(record), phase},
        },
        fact,
        rum(record),
      )
    }
    emitTtftOutcome(record, 'success', NO_REASONS)
    release(record)
  }
  const endContent = (record: TurnRecord, outcome: 'failure' | 'unresolved', reasons: OutcomeReasons) => {
    if (record.ttftFinal) return
    if (record.attributionPending) {
      record.pendingContentEnd = {outcome, reasons}
      return
    }
    record.ttftFinal = true
    emitTtftOutcome(record, outcome, reasons)
    release(record)
  }
  const resolveAttribution = (record: TurnRecord) => {
    record.attributionPending = false
    emitTerminal(record)
    const presentation = record.pendingPresentation
    if (presentation) {
      record.pendingPresentation = undefined
      present(record, presentation)
      return
    }
    const contentEnd = record.pendingContentEnd
    if (contentEnd) {
      record.pendingContentEnd = undefined
      endContent(record, contentEnd.outcome, contentEnd.reasons)
    }
  }
  const onPageExit = (event?: PageTransitionEvent) => {
    if (event?.persisted) return
    for (const record of pending.values()) finalizePending(record, 'unresolved', unresolved('page_exit'))
    for (const record of records.values()) {
      if (record.attributionPending) resolveAttribution(record)
      // Turns with a content receipt are finalized by their presentation view on the same page exit.
      if (!record.contentReceipt) retire(record, 'page_exit')
    }
    pending.clear()
  }
  const installPageExit = () => {
    if (pageExitInstalled || !pageExitTarget) return
    pageExitInstalled = true
    pageExitTarget.addEventListener('pagehide', onPageExit)
  }
  const record = (turnId: string | undefined) => (turnId ? records.get(turnId) : undefined)

  return {
    submitted(submission: TurnSubmission) {
      if (!submission.requestId || !Number.isFinite(submission.submittedAtMs) || !enabled()) return
      finalizedRequests.delete(submission.requestId)
      installPageExit()
      const {modelId, ...rest} = submission
      pending.delete(submission.requestId)
      pending.set(submission.requestId, {...rest, modelFamily: modelFamily(modelId)})
      while (pending.size > MAX_TURN_RECORDS) {
        const oldest = pending.values().next().value
        if (!oldest) break
        finalizePending(oldest, 'unresolved', unresolved('other'))
      }
    },
    discarded(requestId: string) {
      if (!pending.delete(requestId)) return
      rememberFinalizedRequest(finalizedRequests, requestId)
    },
    disposed() {
      for (const pendingRecord of pending.values()) {
        finalizePending(pendingRecord, 'unresolved', unresolved('disposed'))
      }
      for (const turnRecord of records.values()) {
        if (turnRecord.attributionPending) resolveAttribution(turnRecord)
        if (!turnRecord.ttftFinal) finalizeUnresolved(turnRecord, 'disposed')
      }
      pending.clear()
      records.clear()
    },
    dispatched(dispatch: TurnDispatch) {
      if (!dispatch.turnId || !Number.isFinite(dispatch.submittedAtMs) || !enabled()) return
      if (finalizedRequests.has(dispatch.requestId)) return
      installPageExit()
      pending.delete(dispatch.requestId)
      const {modelId, ...rest} = dispatch
      records.delete(dispatch.turnId)
      records.set(dispatch.turnId, {...rest, modelFamily: modelFamily(modelId)})
      recordStart(dispatch)
      while (records.size > MAX_TURN_RECORDS) {
        const oldest = records.values().next().value
        if (!oldest) break
        if (oldest.attributionPending) resolveAttribution(oldest)
        retire(oldest, 'other')
        records.delete(oldest.turnId)
      }
    },
    attributed(turnId: string, taskId: string | undefined, surface: UiReadinessSurface | undefined) {
      const current = record(turnId)
      if (!current) return
      if (taskId) {
        current.taskId = taskId
        recordStart(current)
      }
      if (surface) current.surface = surface
      resolveAttribution(current)
    },
    preDispatchFailed(submission: TurnSubmission) {
      if (!enabled() || finalizedRequests.has(submission.requestId)) return
      const existing = pending.get(submission.requestId)
      if (existing) {
        finalizePending(existing, 'failure', failed('dispatch'))
        return
      }
      const {modelId, ...rest} = submission
      finalizePending({...rest, modelFamily: modelFamily(modelId)}, 'failure', failed('dispatch'))
    },
    acknowledged(turnId: string, atMs: number) {
      const current = record(turnId)
      if (current && current.acknowledgedAtMs === undefined) current.acknowledgedAtMs = atMs
    },
    firstContent(turnId: string, atMs: number) {
      const current = record(turnId)
      if (current && current.firstContentAtMs === undefined) current.firstContentAtMs = atMs
    },
    terminal(turnId: string, outcome: TurnTerminalOutcome, atMs: number) {
      const current = record(turnId)
      if (!current || current.terminalOutcome) return
      current.terminalOutcome = outcome
      current.terminalAtMs = atMs
      emitTerminal(current)
    },
    contentReceipt(fact: ResultPresentationFact) {
      const current = record(fact.turn_id)
      if (!current) return
      current.contentReceipt = true
      current.surfaceObserved = fact.surface
      current.role = fact.role
    },
    // The end is the first content part visibly presented, not its acceptance into client state.
    contentPresented(completion: CompletedPresentation) {
      const current = record(completion.turn_id)
      if (current) present(current, completion)
    },
    contentEnded(turnId: string, outcome: 'failure' | 'unresolved', reasons: OutcomeReasons) {
      const current = record(turnId)
      if (current) endContent(current, outcome, reasons)
    },
    pageExit: onPageExit,
  }
}

export type TurnMetrics = ReturnType<typeof createTurnMetrics>

export const turnMetrics = createTurnMetrics()
